import { lazy, Suspense } from "react";

import {
  BrowserRouter as Router,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";

import { Toaster } from "react-hot-toast";

import { AuthProvider } from "./context/AuthContext";

import { NavigationGuardProvider } from "./context/NavigationGuardContext";

import ProtectedRoute from "./components/ProtectedRoute";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import SkeletonLoader from "./components/SkeletonLoader";
import AdSenseScript from "./components/ads/AdSenseScript";
import NavigationManager from "./components/NavigationManager";

import "./styles/NavigationManager.css";

/*
 * Public pages
 *
 * Ye pages tabhi download honge jab user
 * corresponding route open karega.
 */

const HomePage = lazy(() => import("./pages/HomePage"));

const CategoryPage = lazy(() => import("./pages/CategoryPage"));

const PostDetail = lazy(() => import("./pages/PostDetail"));

const SearchResults = lazy(() => import("./pages/SearchResults"));

const Login = lazy(() => import("./pages/Login"));

// const Signup = lazy(() => import("./pages/Signup"));

const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const Disclaimer = lazy(() => import("./pages/Disclaimer"));

const Contact = lazy(() => import("./pages/Contact"));

const About = lazy(() => import("./pages/About"));

const EditorialPolicy = lazy(() => import("./pages/EditorialPolicy"));

const CorrectionsPolicy = lazy(() => import("./pages/CorrectionsPolicy"));

const NotFound = lazy(() => import("./pages/NotFound"));

/*
 * Admin pages
 *
 * Admin dashboard/editor bundles public users
 * ke initial JavaScript me include nahi honge.
 */

const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

const AllPosts = lazy(() => import("./pages/AllPosts"));

const AddPost = lazy(() => import("./pages/AddPost"));

const EditPost = lazy(() => import("./pages/EditPost"));

/*
 * Shared route loader.
 *
 * Navbar/Footer visible rahenge aur page content
 * load hone tak skeleton show hoga.
 */

const RouteLoader = () => {
  return (
    <div className="route-loader" aria-live="polite" aria-busy="true">
      <span className="route-loader-text">Loading page...</span>

      <div className="route-loader-content">
        <SkeletonLoader variant="article" lines={8} />
      </div>
    </div>
  );
};

/*
 * Public layout.
 *
 * Navbar aur Footer sirf public routes par
 * render honge. Admin routes is layout ke
 * bahar hain.
 */

const PublicLayout = () => {
  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>

      <Navbar />

      <main id="main-content" tabIndex="-1">
        <Suspense fallback={<RouteLoader />}>
          <Outlet />
        </Suspense>
      </main>

      <Footer />
    </>
  );
};

/*
 * Admin route wrapper.
 *
 * Har admin route ke liye repeated
 * ProtectedRoute + Suspense markup avoid hota hai.
 */

const AdminRoute = ({ children }) => {
  return (
    <ProtectedRoute>
      <Suspense fallback={<RouteLoader />}>{children}</Suspense>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <NavigationManager />
        <NavigationGuardProvider>
          <AdSenseScript />
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 2500,

              style: {
                background: "var(--surface)",

                color: "var(--text)",

                border: "1px solid var(--border)",

                borderLeft: "5px solid var(--primary)",

                borderRadius: "12px",

                padding: "14px 18px",

                boxShadow: "var(--shadow-md)",
              },

              success: {
                iconTheme: {
                  primary: "#16a34a",

                  secondary: "#ffffff",
                },
              },

              error: {
                iconTheme: {
                  primary: "#dc2626",

                  secondary: "#ffffff",
                },
              },
            }}
          />

          <Routes>
            {/* Public routes */}

            <Route element={<PublicLayout />}>
              <Route index element={<HomePage />} />

              <Route path="category/:category" element={<CategoryPage />} />

              <Route path="post/:slug" element={<PostDetail />} />

              <Route path="search" element={<SearchResults />} />

              <Route path="login" element={<Login />} />

              {/* <Route path="signup" element={<Signup />} /> */}

              <Route path="privacy-policy" element={<PrivacyPolicy />} />

              <Route path="disclaimer" element={<Disclaimer />} />

              <Route path="contact" element={<Contact />} />

              <Route path="about" element={<About />} />

              <Route path="editorial-policy" element={<EditorialPolicy />} />

              <Route path="corrections-policy" element={<CorrectionsPolicy />} />

              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Protected admin routes */}

            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/posts"
              element={
                <AdminRoute>
                  <AllPosts />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/add-post"
              element={
                <AdminRoute>
                  <AddPost />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/edit/:id"
              element={
                <AdminRoute>
                  <EditPost />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/change-password"
              element={
                <AdminRoute>
                  <ChangePassword />
                </AdminRoute>
              }
            />
          </Routes>
        </NavigationGuardProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
